import { CHANNELS } from "../../shared/channels";
import { BoardSummary, BoardsUpdatedEvent } from "../../shared/boards";
import { call, listen } from "../bridge";
import { locale, t } from "../i18n";
import { byId, flashToast, renderList } from "../dom";
import { amOwner, canUse } from "../me";
import { isViewActive, onViewHidden, onViewShown } from "../navigation";
import { closeBoard, openBoard, openBoardId, setupBoardEditor } from "./whiteboard_board";

// the whiteboard shelf: shared boards everyone draws on, then your own private ones

let boards: BoardSummary[] = [];

function ago(iso: string): string {
  const minutes = Math.round((Date.now() - Date.parse(iso)) / 60_000);
  if (!Number.isFinite(minutes)) return "";
  if (minutes < 1) return t("just now");
  if (minutes < 60) return t("{n}m ago", { n: minutes });
  if (minutes < 60 * 24) return t("{n}h ago", { n: Math.round(minutes / 60) });
  return new Date(iso).toLocaleDateString(locale(), { day: "numeric", month: "short" });
}

function tile(board: BoardSummary): HTMLElement {
  const card = document.createElement("button");
  card.type = "button";
  card.className = "tile wb-tile";
  const media = document.createElement("span");
  media.className = "tile-media";
  media.style.background = board.paper;
  if (board.thumb) {
    const image = document.createElement("img");
    image.alt = "";
    image.src = board.thumb;
    media.append(image);
  } else {
    media.textContent = board.name.slice(0, 1).toUpperCase();
  }
  const title = document.createElement("span");
  title.className = "tile-title";
  title.textContent = board.name;
  const meta = document.createElement("span");
  meta.className = "tile-meta";
  const who = board.editedByName && board.shared ? `${board.editedByName} · ` : "";
  meta.textContent = `${who}${ago(board.updatedAt)}${board.strokes ? ` · ${t("{n} lines", { n: board.strokes })}` : ""}`;
  card.append(media, title, meta);
  if (board.shared) {
    const badge = document.createElement("span");
    badge.className = "tile-badge";
    badge.textContent = t("live");
    card.append(badge);
  }
  card.addEventListener("click", () => void openBoard(board.id));
  return card;
}

function render(): void {
  const shared = boards.filter((board) => board.shared);
  const mine = boards.filter((board) => !board.shared);
  renderList(byId("wb-shared"), shared, tile);
  renderList(byId("wb-mine"), mine, tile);
  byId("wb-shared-empty").hidden = shared.length > 0;
  byId("wb-shared-empty").textContent = amOwner()
    ? t("no shared boards yet. make one and everyone can draw on it together")
    : t("no shared boards yet. Fractal can make one");
  byId("wb-mine-empty").hidden = mine.length > 0;
  byId("wb-count").textContent = boards.length ? t("{shared} shared · {mine} yours", { shared: shared.length, mine: mine.length }) : "";
}

async function reload(): Promise<void> {
  try {
    boards = await call<BoardSummary[]>(CHANNELS.boardsList);
    render();
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not load the boards"));
  }
}

async function create(shared: boolean): Promise<void> {
  const taken = new Set(boards.map((board) => board.name));
  let number = boards.filter((board) => board.shared === shared).length + 1;
  const stem = shared ? t("shared board") : t("board");
  while (taken.has(`${stem} ${number}`)) number++;
  try {
    const made = await call<BoardSummary>(CHANNELS.boardsCreate, { name: `${stem} ${number}`, shared });
    await reload();
    await openBoard(made.id);
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not make a board"));
  }
}

export function setupWhiteboardView(): void {
  setupBoardEditor(() => {
    if (isViewActive("whiteboard")) void reload();
  });
  byId("wb-new").addEventListener("click", () => void create(false));
  byId("wb-new-shared").addEventListener("click", () => void create(true));

  listen<BoardsUpdatedEvent>(CHANNELS.boardsUpdated, (event) => {
    if (event?.removed && event.removed === openBoardId()) {
      closeBoard();
      flashToast(t("that board was deleted"));
    }
    if (isViewActive("whiteboard")) void reload();
  });
  onViewShown("whiteboard", () => {
    byId("wb-new-shared").hidden = !amOwner();
    byId("wb-shared-block").hidden = !canUse("whiteboard", "read");
    void reload();
  });
  onViewHidden("whiteboard", closeBoard);
}
